import { formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { IWordExamVm } from '../../../api/word-exam-vm.model';
import {
  HtmlContentIm,
  PdfGeneratorService,
} from './pdf-generator.service';

@Injectable({ providedIn: 'root' })
export class ExamPdfDocumentService {
  private readonly pdfGeneratorService = inject(PdfGeneratorService);

  generate(exams: IWordExamVm[]): Observable<Blob> {
    const htmlContent: HtmlContentIm = {
      body: this.buildDocument(exams),
      footer: null,
      targetFileName: null,
      pagesTranslation: null,
      ofPagesTranslation: null,
    };

    return this.pdfGeneratorService.post(htmlContent);
  }

  fileName(exams: IWordExamVm[]): string {
    if (exams.length === 1) {
      const exam = exams[0];
      return `Egzamin_wewnętrzny_${this.filePart(
        exam.studentFirstName
      )}_${this.filePart(exam.studentLastName)}.pdf`;
    }

    return `Egzaminy_wewnętrzne_${formatDate(
      new Date(),
      'yyyy-MM-dd',
      'en-GB'
    )}.pdf`;
  }

  private buildDocument(exams: IWordExamVm[]): string {
    const reports = exams
      .map(
        (exam, index) =>
          `<section class="exam-report${
            index < exams.length - 1 ? ' exam-report--with-break' : ''
          }">${this.buildReport(exam)}</section>`
      )
      .join('');

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <style>${this.styles}</style>
        </head>
        <body>${reports}</body>
      </html>
    `;
  }

  private buildReport(exam: IWordExamVm): string {
    const questions = exam.examQuestions ?? [];
    const midpoint = Math.ceil(questions.length / 2);
    const firstTable = questions.slice(0, midpoint);
    const secondTable = questions.slice(midpoint);
    const correct = questions.filter(
      (question) => question.userGiveCorrectAnswer === true
    ).length;
    const incorrect = questions.length - correct;
    const passed = exam.earnedPoints >= exam.minimumPoints;

    return `
      <div class="main-section">
        <p class="exam-date-field">Data: ${
          exam.end ? formatDate(exam.end, 'dd.MM.yyyy HH:mm', 'en-GB') : '—'
        }</p>
        <p class="text-center text-base">
          Arkusz przebiegu części teoretycznej egzaminu wewnętrznego
        </p>
        <p class="font-semibold text-center text-base">
          Kategoria: ${this.escape(exam.examCategory)}
        </p>

        <div class="details-container">
          <div class="details-column">
            ${this.detail('Imię:', exam.studentFirstName)}
            ${this.detail('Nazwisko:', exam.studentLastName)}
            ${this.detail('Pesel:', exam.studentPesel)}
            ${this.detail('PKK:', exam.studentPKK)}
          </div>
          <div class="details-column">
            ${this.detail('Liczba pytań:', questions.length)}
            ${this.detail('ID egzaminu:', exam.guid.slice(-6))}
          </div>
        </div>

        <p class="text-center text-base font-semibold ${
          passed ? 'text-green' : 'text-red'
        }">
          ${passed ? 'Egzamin zaliczony' : 'Egzamin niezaliczony'}
        </p>

        <div class="details-container">
          <div class="details-column">
            ${this.detail('Poprawne odpowiedzi:', correct)}
            ${this.detail('Błędne odpowiedzi:', incorrect)}
            ${this.detail('Liczba uzyskanych punktów:', exam.earnedPoints)}
          </div>
          <div class="details-column">&nbsp;</div>
        </div>
      </div>

      <div class="question-grid">
        ${this.questionTable(firstTable, 0)}
        ${this.questionTable(secondTable, firstTable.length)}
      </div>

      <div class="footer-section">
        <div class="signature-column">
          <div class="separator">&nbsp;</div>
          <p class="signature">Podpis osoby egzaminowanej</p>
        </div>
        <div class="signature-column">
          <div class="separator">&nbsp;</div>
          <p class="signature">
            Podpis i pieczęć instruktora lub wykładowcy przeprowadzającego egzamin
          </p>
        </div>
      </div>
    `;
  }

  private questionTable(
    questions: NonNullable<IWordExamVm['examQuestions']>,
    indexOffset: number
  ): string {
    const rows = questions
      .map(
        (question, index) => `
          <tr>
            <td>${indexOffset + index + 1}</td>
            <td>${this.escape(question.questionNumber)}</td>
            <td>${this.escape(question.correctAnswer)}</td>
            <td>${
              question.userGiveCorrectAnswer === true ? 'Dobra' : 'Zła'
            }</td>
            <td>${question.examPoints ?? 0}</td>
          </tr>
        `
      )
      .join('');

    return `
      <table class="styled-print-table">
        <thead>
          <tr>
            <th>Lp.</th>
            <th>ID</th>
            <th>Klucz odpowiedzi</th>
            <th>Udzielona odpowiedź</th>
            <th>Punkty</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  private detail(label: string, value: unknown): string {
    return `
      <div class="details-row">
        <p>${label}</p>
        <p>${this.escape(value)}</p>
      </div>
    `;
  }

  private escape(value: unknown): string {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  private filePart(value: string | undefined): string {
    return (value?.trim() || 'kursant').replace(/[^\p{L}\p{N}-]+/gu, '_');
  }

  private readonly styles = `
    @page { margin: 0; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 16px;
      color: #000;
      font-family: Inter, Arial, sans-serif;
      font-size: 12px;
      font-weight: 400;
      line-height: normal;
    }
    p { margin: 0; padding: 0; }
    .exam-report--with-break { page-break-after: always; break-after: page; }
    .main-section { display: flex; flex-direction: column; gap: 16px; }
    .exam-date-field { font-size: 11px; font-weight: 600; }
    .text-center { text-align: center; }
    .text-base { font-size: 16px; }
    .font-semibold { font-weight: 600; }
    .text-green { color: #198754; }
    .text-red { color: #ef4444; }
    .details-container { display: flex; flex-direction: row; gap: 52px; }
    .details-column {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .details-row { display: flex; justify-content: space-between; gap: 8px; }
    .question-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-top: 16px;
    }
    .styled-print-table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #d1d5db;
    }
    .styled-print-table th {
      padding: 4px;
      border: 1px solid #333;
      font-size: 11px;
      font-weight: 600;
      line-height: 24px;
      text-align: center;
    }
    .styled-print-table td {
      padding: 0 4px;
      border: 1px solid #f3f4f6;
      color: #1e1f22;
      font-size: 12px;
      font-weight: 300;
      line-height: 24px;
      text-align: center;
    }
    .styled-print-table tbody tr:nth-child(even) { background: #f9fafb; }
    .styled-print-table td:first-child {
      border: 1px solid #333;
      color: #000;
      font-weight: 600;
    }
    .styled-print-table td:nth-child(4) { font-weight: 600; }
    .footer-section { display: flex; gap: 24px; margin-top: 100px; }
    .signature-column { width: 100%; }
    .separator { height: 1px; margin-bottom: 4px; background: #d1d5db; }
    .signature { font-size: 10px; text-align: center; }
  `;
}
